import { useState, useEffect } from 'react';
import {
  BOARD_STYLES,
  PIECE_SETS,
  getChessPreferences,
  saveChessPreferences,
} from '../../services/chessPreferencesService';
import './ChessSettings.css';

const ChessSettings = ({ user, onClose, onPreferencesChange }) => {
  const [preferences, setPreferences] = useState({
    boardStyle: 'brown',
    pieceSet: 'neo',
    useImages: true,
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('board'); // 'board' or 'pieces'

  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    const prefs = await getChessPreferences(user.uid);
    setPreferences(prefs);
  };

  const handleBoardStyleChange = (styleId) => {
    const newPrefs = { ...preferences, boardStyle: styleId };
    setPreferences(newPrefs);
    if (onPreferencesChange) {
      onPreferencesChange(newPrefs);
    }
  };

  const handlePieceSetChange = (setId) => {
    const newPrefs = { ...preferences, pieceSet: setId };
    setPreferences(newPrefs);
    if (onPreferencesChange) {
      onPreferencesChange(newPrefs);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveChessPreferences(user.uid, preferences);
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chess-settings-overlay">
      <div className="chess-settings-dialog">
        <div className="chess-settings-header">
          <h2>Chess Customization</h2>
          <button className="close-settings-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="chess-settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'board' ? 'active' : ''}`}
            onClick={() => setActiveTab('board')}
          >
            Board Style
          </button>
          <button
            className={`settings-tab ${activeTab === 'pieces' ? 'active' : ''}`}
            onClick={() => setActiveTab('pieces')}
          >
            Piece Set
          </button>
        </div>

        <div className="chess-settings-content">
          {activeTab === 'board' && (
            <div className="board-styles-grid">
              {BOARD_STYLES.map((style) => (
                <div
                  key={style.id}
                  className={`board-style-option ${
                    preferences.boardStyle === style.id ? 'selected' : ''
                  }`}
                  onClick={() => handleBoardStyleChange(style.id)}
                >
                  <div className="board-preview">
                    <div
                      className="preview-square light"
                      style={{ backgroundColor: style.colors.light }}
                    ></div>
                    <div
                      className="preview-square dark"
                      style={{ backgroundColor: style.colors.dark }}
                    ></div>
                    <div
                      className="preview-square dark"
                      style={{ backgroundColor: style.colors.dark }}
                    ></div>
                    <div
                      className="preview-square light"
                      style={{ backgroundColor: style.colors.light }}
                    ></div>
                  </div>
                  <span className="style-name">{style.name}</span>
                  {preferences.boardStyle === style.id && (
                    <span className="selected-check">✓</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'pieces' && (
            <div className="piece-sets-grid">
              {PIECE_SETS.map((set) => (
                <div
                  key={set.id}
                  className={`piece-set-option ${
                    preferences.pieceSet === set.id ? 'selected' : ''
                  }`}
                  onClick={() => handlePieceSetChange(set.id)}
                >
                  <div className="piece-preview">
                    {set.id === 'unicode' ? (
                      <div className="unicode-preview">
                        <span className="piece-symbol">♘</span>
                        <span className="piece-symbol">♕</span>
                      </div>
                    ) : (
                      <div className="image-preview">
                        <div className="preview-pieces-container">
                          <img
                            src={`/chess/pieces/${set.id}/wn.png`}
                            alt={`${set.name} knight`}
                            className="preview-piece-image"
                            onError={(e) => {
                              e.target.parentElement.style.display = 'none';
                              e.target.parentElement.nextSibling.style.display = 'flex';
                            }}
                          />
                          <img
                            src={`/chess/pieces/${set.id}/wq.png`}
                            alt={`${set.name} queen`}
                            className="preview-piece-image"
                          />
                        </div>
                        <div className="preview-piece-placeholder" style={{ display: 'none' }}>
                          {set.name.charAt(0)}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="style-name">{set.name}</span>
                  {preferences.pieceSet === set.id && (
                    <span className="selected-check">✓</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="chess-settings-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save & Apply'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChessSettings;
